import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

import * as dat from 'dat.gui';
import axios from 'axios';
import gsap from 'gsap';
import { parse } from 'papaparse';

const site = 'https://gruze.org/fly';
//const site = '';

const alphabet: any ={'alf':'α','alf01':'α¹','alf02':'α²',
          'bet':'β','bet01':'β¹','bet02':'β²','bet03':'β³',
          'gam':'γ','gam01':'γ¹','gam02':'γ²','gam03':'γ³',
          'del':'δ','del01':'δ¹','del02':'δ²','del03':'δ³',
          'eps':'ε','eps01':'ε¹','eps02':'ε²','eps03':'ε³',
          'zet':'ζ','zet01':'ζ¹','zet02':'ζ²','zet03':'ζ³',
          'eta':'η','eta01':'η¹','eta02':'η²','eta03':'η³',
          'the':'θ','the01':'θ¹','the02':'θ²','the03':'θ³',
          'tet':'θ','tet01':'θ¹','tet02':'θ²','tet03':'θ³',
          'iot':'ι','iot01':'ι¹','iot02':'ι²',
          'kap':'κ','kap01':'κ¹','kap02':'κ²',
          'lam':'λ','lam01':'λ¹','lam02':'λ²','lam03':'λ³',
          'mu':'μ','mu01':'μ¹','mu02':'μ²','mu03':'μ³',
          'nu':'ν','nu01':'ν¹','nu02':'ν²','nu03':'ν³',
          'ksi':'ξ','ksi01':'ξ¹','ksi02':'ξ²','ksi03':'ξ³',
          'omi':'ο','omi01':'ο¹','omi02':'ο²','omi03':'ο³',
          'pi':'π','pi01':'π¹','pi02':'π²','pi03':'π³','pi04':'π⁴','pi05':'π⁵','pi06':'π⁶',
          'rho':'ρ','rho01':'ρ¹','rho02':'ρ²','rho03':'ρ³',
          'sig':'σ','sig01':'σ¹','sig02':'σ²','sig03':'σ³',
          'tau':'τ','tau01':'τ¹','tau02':'τ²','tau03':'τ³','tau04':'τ⁴','tau05':'τ⁵','tau06':'τ⁶','tau07':'τ⁷','tau08':'τ⁸','tau09':'τ⁹',
          'ups':'υ','ups01':'υ¹','ups02':'υ²','ups03':'υ³',
          'phi':'φ','phi01':'φ¹','phi02':'φ²','phi03':'φ³','phi04':'φ⁴',
          'chi':'χ','chi01':'χ¹','chi02':'χ²','chi03':'χ³',
          'psi':'ψ','psi01':'ψ¹','psi02':'ψ²','psi03':'ψ³','psi04':'ψ⁴','psi05':'ψ⁵','psi06':'ψ⁶','psi07':'ψ⁷','psi08':'ψ⁸','psi09':'ψ⁹',
          'ome':'ω','ome01':'ω¹','ome02':'ω²','ome03':'ω³'}

const short_lookup: any = {
    'alf':'alpha',
    'bet':'beta',
    'gam':'gamma',
    'del':'delta',
    'eps':'epsilon',
    'zet':'zeta',
    'eta':'eta',
    'tet':'theta',
    'iot':'iota',
    'kap':'kappa',
    'lam':'lambda',
    'mu':'mu',
    'nu':'nu',
    'ksi':'xi',
    'omi':'omicron',
    'pi.':'pi',
    'rho':'rho',
    'sig':'sigma',
    'tau':'tau',
    'ups':'upsilon',
    'phi':'phi',
    'chi':'chi',
    'psi':'psi',
    'ome':'omega'
}

let doSpin: boolean = false;

let script: any;
let doSetScriptText: any;

const filterOptions = [
    {name:'Clusters and moving groups', children: [
      { itemCode: 'C1', itemLabel: 'Hyades'},
      { itemCode: 'C2', itemLabel: 'Coma'},
      { itemCode: 'C3', itemLabel: 'Tucana and Horologium association'},
      { itemCode: 'C4', itemLabel: '32 Ori'},
      { itemCode: 'C5', itemLabel: 'eta Cha'},
      { itemCode: 'C6', itemLabel: 'Ursa Major moving group'},
      { itemCode: 'C7', itemLabel: 'AB Dor moving group'},
      { itemCode: 'C8', itemLabel: 'beta Pic moving group'},
      { itemCode: 'C9', itemLabel: 'Carina Near moving group'},
    ]},
    {name:'Stars by type', children: [
      { itemCode: 'O', itemLabel: 'Intrinsically hot OBA stars'},
      { itemCode: 'G', itemLabel: 'Solar like stars'},
      { itemCode: 'M', itemLabel: 'Example main sequence stars'},
      { itemCode: 'W', itemLabel: 'White dwarfs'},
      { itemCode: 'R', itemLabel: 'Red giants'},
      { itemCode: 'S', itemLabel: 'Sub dwarfs'},
      { itemCode: 'U', itemLabel: 'Ultra cool dwarfs'},
      { itemCode: 'B', itemLabel: 'Wide binary systems'},
      { itemCode: 'P', itemLabel: 'Stars with known planets'},
    ]},
    {name:'Stars by origin', children: [
      { itemCode: 'H', itemLabel: 'Halo objects'},
      { itemCode: 'I', itemLabel: 'Inner galaxy objects'},
      { itemCode: 'T', itemLabel: 'Thick disk and halo'},
      { itemCode: 'E', itemLabel: 'Enceladus stream'},
    ]},
    {name:'Other', children: [
      { itemCode: 'J', itemLabel: 'Nearby 10 parsecs sample'},
      { itemCode: 'N', itemLabel: 'Nearby 25 parsecs sample'},
      { itemCode: 'D', itemLabel: 'Distant objects at the 100 parsecs border'},
      { itemCode: 'V', itemLabel: 'Stars visible to the eye'},
    ]},
    {name:'Kinematical strips', children: [
      { itemCode: 'K1', itemLabel: 'K1 strip'},
      { itemCode: 'K2', itemLabel: 'K2 strip'},
      { itemCode: 'K3', itemLabel: 'K3 strip'},
      { itemCode: 'K4', itemLabel: 'K4 strip'},
      { itemCode: 'K5', itemLabel: 'K5 strip'},
      { itemCode: 'K6', itemLabel: 'K6 strip'},
    ]},
    {name:'Non-GCNS stars', children: [
      { itemCode: 'ZO', itemLabel: 'Intrinsically hot OBA stars'},
      { itemCode: 'ZJ', itemLabel: 'Nearby 10 parsecs sample'},
      { itemCode: 'ZN', itemLabel: 'Nearby 25 parsecs sample'},
      { itemCode: 'ZZ', itemLabel: 'Other non-GCNS stars'}
    ]},
];

const colors = [
    [0.55, 0.9, 0.7], // blue
    [0.0, 0.0, 1.0], // white
    [0.14, 0.9, 0.85], // light yellow
    [0.18, 1.0, 0.7], // yellow
    [0.09, 1.0, 0.7], //orange
    [0.0, 1.0, 0.7] // red
];

const sizes = [16, 12, 8, 6, 4, 2];

type GeometrySet = {
    [key:string]: Array<THREE.BufferGeometry>
}

type PointSet = {
    [key:string]: Array<THREE.Points>
}

let star_point: any;
let star_point_clicked: any;

const star_scale_default = 5;

let ball_scale = 0.002*star_scale_default;
let dir_scale = star_scale_default/4;
const far_clip = 75*10;

const mesh_loader = new GLTFLoader();

const search_geometry = new THREE.SphereGeometry( 3*star_scale_default*ball_scale, 32, 32 );
const search_material = new THREE.MeshBasicMaterial( {color: 0xccffff, transparent: true, opacity: 0.2} );
const search_sphere = new THREE.Mesh( search_geometry, search_material );

search_sphere.visible = false;

const sun_geometry = new THREE.SphereGeometry( 0.9*star_scale_default*ball_scale, 32, 32 );
const sun_material = new THREE.MeshBasicMaterial( {color: 0xffff00} );
const sun_sphere = new THREE.Mesh( sun_geometry, sun_material );
sun_sphere.position.x = 0;
sun_sphere.position.y = 0;
sun_sphere.position.z = 0;

const pc5_geometry = new THREE.SphereGeometry( 5, 32, 32 );
const pc5_material = new THREE.MeshBasicMaterial( {color: 0xccffcc, transparent: true, opacity: 0.2} );
const pc5_sphere = new THREE.Mesh( pc5_geometry, pc5_material );
pc5_sphere.position.x = 0;
pc5_sphere.position.y = 0;
pc5_sphere.position.z = 0;

pc5_sphere.visible = false;

const pc10_geometry = new THREE.SphereGeometry( 10, 32, 32 );
const pc10_material = new THREE.MeshBasicMaterial( {color: 0xccffcc, transparent: true, opacity: 0.2} );
const pc10_sphere = new THREE.Mesh( pc10_geometry, pc10_material );
pc10_sphere.position.x = 0;
pc10_sphere.position.y = 0;
pc10_sphere.position.z = 0;

pc10_sphere.visible = false;

const pc25_geometry = new THREE.SphereGeometry( 25, 32, 32 );
const pc25_material = new THREE.MeshBasicMaterial( {color: 0xccffcc, transparent: true, opacity: 0.2} );
const pc25_sphere = new THREE.Mesh( pc25_geometry, pc25_material );
pc25_sphere.position.x = 0;
pc25_sphere.position.y = 0;
pc25_sphere.position.z = 0;

pc25_sphere.visible = false;

const mesh_names = ['32_ori','tha','eta_cha','hyades','uma','coma'];
const mesh_names_extra = ['sun','pc5','pc10','pc25']

let mesh_list: any = {'sun':sun_sphere,'pc5':pc5_sphere,'pc10':pc10_sphere,'pc25':pc25_sphere};

const threshold = 0.1;
let mouse = new THREE.Vector2();
let intersection = null;

let camera: THREE.PerspectiveCamera;
let scene: THREE.Scene;
let renderer: THREE.WebGLRenderer;
let controls: OrbitControls;

let raycaster = new THREE.Raycaster();
raycaster!.params!.Points!.threshold = threshold;
//let parameters: any;
let mouseX = 0, mouseY = 0;

let windowHalfX = window.innerWidth / 2;
let windowHalfY = window.innerHeight / 2;

let constellations: any = {};

let materials: any = [];

const textureLoader = new THREE.TextureLoader();
const ball = textureLoader.load('textures/sprites/ball_inverted.png');

// settings should allow adjusting scale and clip

let va_all: any = {};
let gs_all: GeometrySet = {};
let ps_all: PointSet = {}

let g_keys: any = [];

function getFilterGroups(items: Array<string>) {
    let groups: any = {};
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        for (let j = 0; j < filterOptions.length; j++) {
            const g = filterOptions[j];
            for (let k = 0; k < g.children.length; k++) {
                const c = g.children[k];
                if (item == c.itemCode) {
                    if (!groups.hasOwnProperty(g.name)) {
                        groups[g.name] = [];
                    }
                    groups[g.name].push(c.itemLabel)
                }
            }            
        }
    }
    return groups;
}

function filterStars(a: Array<string>) {
    let state: boolean;
    for (let j = 0; j < g_keys.length; j++) {
        if (a.includes(g_keys[j])) {
            state = true;
        } else {
            state = false;
        }

        for (let i = 0; i < 36; i++) {
            ps_all[g_keys[j]][i].visible = state;
        }
    }
}

function filterMeshes(a: Array<string>) {
    let state: boolean;
    //console.log('all mesh names',mesh_names);
    const all_mesh_names = mesh_names.concat(mesh_names_extra);
    //console.log('all mesh names',all_mesh_names);
    for (let j = 0; j < all_mesh_names.length; j++) {
        if (a.includes(all_mesh_names[j])) {
            state = true;
        } else {
            state = false;
        }

        mesh_list[all_mesh_names[j]].visible = state;
    }
}

function filterStarsScript(a: Array<string>, hide: boolean) {
    let state: boolean;
    let all = a[0] == 'all';
    for (let j = 0; j < g_keys.length; j++) {
        if (all || a.includes(g_keys[j])) {
            state = hide?false:true;
            for (let i = 0; i < 36; i++) {
                ps_all[g_keys[j]][i].visible = state;
            }
        } 
    }
}

function filterMeshesScript(a: Array<string>, hide: boolean) {
    let state: boolean;
    let all = a[0] == 'all';
    console.log('all mesh names',mesh_names);
    const all_mesh_names = mesh_names.concat(mesh_names_extra);
    console.log('all mesh names',all_mesh_names);
    for (let j = 0; j < all_mesh_names.length; j++) {
        if (all || a.includes(all_mesh_names[j])) {
            state = hide?false:true;
            mesh_list[all_mesh_names[j]].visible = state;
        }
    }
}

function setScriptFile(sf: string, scriptImmediate: string) {
    //let s = [];
    // const ss = sf.trim().split("\n");
    // const fieldNames: Array<string> = ss[0].split(',');
    // for (let i = 1; i < ss.length; i++) {
    //     const bits = ss[i].split(',');
    //     let item: any = {};
    //     if (bits.length == fieldNames.length) {
    //         for (let j = 0; j < fieldNames.length; j++) {            
    //             item[fieldNames[j].trim()] = bits[j].trim();
    //         }
    //         s.push(item);
    //     }
    // }
    // script = s;
    script = parse(sf.trim(),{header: true}).data;
    console.log('new script',JSON.stringify(script));
    if (scriptImmediate == '1') {
        playScript();
    }
}

function setStarScale(s: string) {
    const starScale = parseInt(s);
    ball_scale = 0.002*starScale;
    let parameters: any = [];

    for (let i = 0; i < colors.length; i++) {
        for (let j = 0; j < sizes.length; j++) {
            parameters.push([colors[i], ball, ball_scale*sizes[j]]);
        }
    }

    for (let j = 0; j < g_keys.length; j++) {
        for (let i = 0; i < parameters.length; i++) {

            // const color = parameters[i][0];
            // const sprite = parameters[i][1];
            const size = parameters[i][2];

            // const material = new THREE.PointsMaterial({ size: size, map: sprite, depthTest: true, transparent: false, alphaTest: 0.5 });
            // material.color.setHSL(color[0], color[1], color[2]);

            (ps_all[g_keys[j]][i].material as any).size = size;
            (ps_all[g_keys[j]][i].material as any).needsUpdate = true;

            const scale = starScale/star_scale_default;

            sun_sphere.scale.set(scale,scale,scale);

            search_sphere.scale.set(scale,scale,scale);
            
            dir_scale = starScale/2;
        }
    }
}

function mouseMoveWhilstDown(target: any, whileMove: any) {
    var endMove = function () {
        target.removeEventListener('mousemove', whileMove);
        target.removeEventListener('mouseup', endMove);
    };

    target.addEventListener('mousedown', function (event: any) {
        event.stopPropagation(); // remove if you do want it to propagate ..
        target.addEventListener('mousemove', whileMove);
        target.addEventListener('mouseup', endMove);   
    });
}

function init(setScriptText: any) {

    doSetScriptText = setScriptText;   

    //camera = new THREE.PerspectiveCamera( 75, window.innerWidth / window.innerHeight, 1, 2000 );
    //camera.position.z = 1000;

    camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.25, far_clip);
    camera.position.z = 300;

    scene = new THREE.Scene();

    scene.add( search_sphere );
    scene.add( sun_sphere );
    scene.add( pc5_sphere );
    scene.add( pc10_sphere );
    scene.add( pc25_sphere );
    // scene.fog = new THREE.FogExp2(0x000000, 0.0008);

    const light = new THREE.AmbientLight( 0x404040, 50 ); // soft white light
    scene.add( light );

    // const spotLight = new THREE.SpotLight( 0xffffff, 500 );
    // spotLight.position.set( 0, 2000, 0 );
    // scene.add( spotLight );

    renderer = new THREE.WebGLRenderer();
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    document.getElementById('root')!.appendChild(renderer.domElement);
    renderer.domElement.id = 'image-container';
    controls = new OrbitControls( camera, renderer.domElement );

    //const overlay: any =  <div style={{zIndex:5, paddingTop:100, position:'absolute'}}><p style={{color:'white'}}>Testing z index</p></div>
    //document.querySelector('#image-container')!.append(overlay);   

    for (let i = 0; i < mesh_names.length; i++) {
        mesh_loader.load(
            // resource URL
            site+'/mesh_bits/'+mesh_names[i]+'.glb',
            // called when the resource is loaded
            function ( gltf ) {
                console.log('meshes loaded');
                let model = gltf.scene;
                
                let newMaterial = new THREE.MeshStandardMaterial({color: 0x99ff99, transparent: true, opacity: 0.2});
                model.traverse((o: any) => {
                    if (o.isMesh) {
                        o.visible = false;
                        mesh_list[mesh_names[i]] = o;
                        o.material = newMaterial;
                    }
                });
                scene.add( gltf.scene );
            },
            function ( xhr ) {
                console.log( ( xhr.loaded / xhr.total * 100 ) + '% loaded' );
            },
            function ( error ) {
                console.log( 'A mesh loader error occurred', error );
            }
        );
    }

    axios({
        url: site+'/constellations.json',
        method: 'GET',
        responseType: 'json', // important
    }).then((response) => {
        let json = response.data;
        for (let i = 0; i < json.length; i++) {
            constellations[json[i].abbr] = json[i].genitive;
        }

        axios({
            url: site+'/gcns4_plus_new_10pc.csv?v=6',
            method: 'GET',
            responseType: 'text', // important
        }).then((response) => {
            const csv = response.data;
            console.log('loaded '+csv.length+' bytes');
            const stars = csv.split('\n');
            let count = 0;
            
            for (let i = 0; i < stars.length; i++) {
                const star = stars[i].trim();
                const d = star.split(',')
                if (d.length > 1) {
                    const x = parseFloat(d[0]);
                    const z = -parseFloat(d[1]);
                    const y = parseFloat(d[2]);
                    const mi = parseInt(d[3]);
                    const vs = d[4].split('|');

                    for (let j = 0; j < vs.length; j++) {
                        if (!va_all.hasOwnProperty(vs[j])) {
                            g_keys.push(vs[j]);
                            va_all[vs[j]] = new Array<Array<number>>(36);
                            for (let k = 0; k < 36; k++) {
                                va_all[vs[j]][k] = [];
                            }
                        }
                        va_all[vs[j]][mi].push(x,y,z);
                        count += 1;
                    }
                }
            }
            console.log('g keys number: '+g_keys.length);
            console.log('g keys: ',g_keys);
            console.log('point total: '+count);

            for (let j = 0; j < g_keys.length; j++) {
                if (!gs_all.hasOwnProperty(g_keys[j])) {
                    gs_all[g_keys[j]] = new Array<THREE.BufferGeometry>(36);
                    for (let k = 0; k < 36; k++) {
                        gs_all[g_keys[j]][k] = new THREE.BufferGeometry();
                    }
                }
                console.log('setting ', g_keys[j]);
                for (let i = 0; i < 36; i++) {
                    gs_all[g_keys[j]][i].setAttribute('position', new THREE.Float32BufferAttribute(va_all[g_keys[j]][i], 3));
                }
            }

            let parameters: any = [];

            for (let i = 0; i < colors.length; i++) {
                for (let j = 0; j < sizes.length; j++) {
                    parameters.push([colors[i], ball, ball_scale*sizes[j]]);
                }
            }

            count = 0;

            for (let j = 0; j < g_keys.length; j++) {
                for (let i = 0; i < parameters.length; i++) {

                    const color = parameters[i][0];
                    const sprite = parameters[i][1];
                    const size = parameters[i][2];

                    materials[i] = new THREE.PointsMaterial({ size: size, map: sprite, depthTest: true, transparent: false, alphaTest: 0.5 });
                    materials[i].color.setHSL(color[0], color[1], color[2]);

                    const particles = new THREE.Points(gs_all[g_keys[j]][i], materials[i]);

                    scene.add(particles);

                    count += 1;

                    if (!ps_all.hasOwnProperty(g_keys[j])) {
                        ps_all[g_keys[j]] = new Array<THREE.Points>(36);
                    }
                    ps_all[g_keys[j]][i] = particles;
                }
            }

            console.log('particle system count',count);

            //

            //stats = new Stats();
            //document.body.appendChild( stats.dom );

            //

            const gui = new dat.GUI();

            const params = {
                texture: true
            };

            gui.add(params, 'texture').onChange(function (value) {

                for (let i = 0; i < materials.length; i++) {

                    materials[i].map = (value === true) ? parameters[i][1] : null;
                    materials[i].needsUpdate = true;
                }
            });

            gui.open();

            document.body.style.touchAction = 'none';

            // Sirius
            const search_x = -1.769;
            const search_y = -0.408;
            const search_z = -1.913;

            const search_data: any = {"gaia_id":"Gaia EDR3 48026706558487040","id_2mass":"2MASS J04283697+1910494","simbad_name":"eps Tau","simbad_xmatch":"gcns","simbad_common":"Ain","simbad_spt":"G9.5IIICN0.5","flythrucode":"V|C1|P|B","g":"3.2804532","m":"0.028752417386683327","b_r":"1.2369775","xg":"-41.991","yg":"1.762","zg":"-15.233"}

            // Canopus
            // const search_x = -13.093;
            // const search_y = -40.496;
            // const search_z = -84.694;

            // Arcturus        		

            // sphere.position.x = 3.876;
            // sphere.position.y = 10.518;
            // sphere.position.z = 1.042;

            document.getElementById('searchField')!.addEventListener('keydown', handleSearchBox, false);
            document.getElementById('search-cancel')!.addEventListener('click', handleSearchCancel, false);
            document.getElementById('appbar-home')!.addEventListener('click', handleSearchCancel, false);

            renderer.domElement.addEventListener('dblclick',handleCanvasDblClick,false);
            document.addEventListener('keydown', handleKeyPress, false);

            //document.getElementById('script-file')!.addEventListener('change', handleScriptFile, false);

            //searchEffect("Proxima Cen");

            window.addEventListener('resize', onWindowResize, false);
            document.addEventListener( 'mousemove', onDocumentMouseMove, false );

            //TODO: add cyan sphere around -1.769	-1.913	-0.408

            // const options = ((document.getElementById('gaia-filter')!) as HTMLInputElement).value;
            // console.log('filter options',options);
            // if(options) {
            //     const option_bits = options.split('|');
            //     filterStars(option_bits);
            // }
            //window.addEventListener('wheel', onDocumentMouseWheel, false);
        }).catch(function (error) {
            if (error.response) {
                // Request made and server responded
                console.log(error.response.data);
                console.log(error.response.status);
                console.log(error.response.headers);
            } else if (error.request) {
                // The request was made but no response was received
                console.log(error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log('Error', error.message);
            }
        });
    });
}

function playScript() {
    handleScript(script,
        {
            'Sun':{xg:'0',yg:'0',zg:'0'},
            'Out':{xg:'-300',yg:'0',zg:'0'},
            'In':{xg:'300',yg:'0',zg:'0'},
            'Top':{xg:'0',yg:'0',zg:'300'},
            'Left':{xg:'0',yg:'300',zg:'0'},
            'Right':{xg:'0',yg:'-300',zg:'0'},
            'Bottom':{xg:'0',yg:'0',zg:'-300'},
            'NearOut':{xg:'-100',yg:'0',zg:'0'},
            'NearIn':{xg:'100',yg:'0',zg:'0'},
            'NearTop':{xg:'0',yg:'0',zg:'100'},
            'NearLeft':{xg:'0',yg:'100',zg:'0'},
            'NearRight':{xg:'0',yg:'-100',zg:'0'},
            'NearBottom':{xg:'0',yg:'0',zg:'-100'},
            'CloseOut':{xg:'-25',yg:'0',zg:'0'},
            'CloseIn':{xg:'25',yg:'0',zg:'0'},
            'CloseTop':{xg:'0',yg:'0',zg:'25'},
            'CloseLeft':{xg:'0',yg:'25',zg:'0'},
            'CloseRight':{xg:'0',yg:'-25',zg:'0'},
            'CloseBottom':{xg:'0',yg:'0',zg:'-25'}
        }
    );
}

function handleKeyPress(e: any) {
    console.log('handleKeyPress',e);
    if((e.key === "p") && script) {
        playScript();
    }
}

function handleScript(s: any, d: any) {
    if (s.length > 0) {
        const head = s[0];
        const tail = s.slice(1);
        if (head.action == 'wait') {
            handleScriptWait(head,tail,d);
        } else if (head.action == 'rotate') {
            handleScriptRotate(head,tail,d);
        } else if (head.action == 'fly') {
            handleScriptFly(head,tail,d);
        } else if (head.action == 'spin') {
            handleScriptSpin(head,tail,d);
        } else if (head.action == 'text') {
            handleScriptText(head,tail,d);
        } else if (head.action == 'bottomtext') {
            handleScriptBottomText(head,tail,d);
        } else if (head.action == 'meshes') {
            handleScriptMeshes(head,tail,d);
        } else if (head.action == 'stars') {
            handleScriptStars(head,tail,d);
        }  else {
            console.log('Script error: unknown action',head.action);
        }
    } else {
        // remove any left over bottom text
        doSetScriptText('',false,'bottom');
    }
}

function handleScriptMeshes(item: any, s:any, d:any) {
    if (item.duration == 'show') {
        filterMeshesScript(item.target.split('|'),false);
    } else {
        filterMeshesScript(item.target.split('|'),true);
    }
    handleScript(s,d);
}

function handleScriptStars(item: any, s:any, d:any) {
    if (item.duration == 'show') {
        filterStarsScript(item.target.split('|'),false);
    } else {
        filterStarsScript(item.target.split('|'),true);
    }
    handleScript(s,d);
}

function handleScriptText(item: any, s:any, d:any) {
    const duration = parseInt(item.duration);
    doSetScriptText(item.target,true,'center');
    setTimeout(() => { doSetScriptText('',false,'center'); handleScript(s,d) }, duration*1000);
}

function handleScriptBottomText(item: any, s:any, d:any) {
    const duration = parseInt(item.duration);
    if (item.target == '') {
        doSetScriptText('',false,'bottom');
        handleScript(s,d);
    } else {
        doSetScriptText(item.target,true,'bottom');
        if (duration == 0) {
            handleScript(s,d);
        } else {
            setTimeout(() => { doSetScriptText('',false,'bottom'); handleScript(s,d) }, duration*1000);
        }
    }
}

function handleScriptWait(item: any, s:any, d:any) {
    const duration = parseInt(item.duration);
    setTimeout(() => { handleScript(s,d) }, duration*1000);
}

function handleScriptSpin(item: any, s:any, d:any) {
    const duration = parseInt(item.duration);
    doSpin = true;
    controls.autoRotate = true;
    setTimeout(() => { controls.autoRotate = false; doSpin = false; handleScript(s,d) }, duration*1000);
}

function handleScriptRotate(item: any, s:any, d:any) {
    const duration = parseInt(item.duration);
    const target = item.target;
    if (d.hasOwnProperty(target)) {
        doRotate(item,s,d);
    } else {
        axios({
            url: 'https://gruze.org:3009/query?object='+encodeURIComponent(item.target),
            method: 'GET',
            responseType: 'json', // important
        }).then((response) => {
            d[item.target] = response.data;
            doRotate(item,s,d);
        }).catch(function (error) {
            if (error.response) {
                // Request made and server responded
                console.log(error.response.data);
                console.log(error.response.status);
                console.log(error.response.headers);
            } else if (error.request) {
                // The request was made but no response was received
                console.log(error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log('Error', error.message);
            }
        });
    }    
}

function handleScriptFly(item: any, s:any, d:any) {
    const target = item.target;
    if (d.hasOwnProperty(target)) {
        doFly(item,s,d);
    } else {
        axios({
            url: 'https://gruze.org:3009/query?object='+encodeURIComponent(item.target),
            method: 'GET',
            responseType: 'json', // important
        }).then((response) => {
            d[item.target] = response.data;
            doFly(item,s,d);
        }).catch(function (error) {
            if (error.response) {
                // Request made and server responded
                console.log(error.response.data);
                console.log(error.response.status);
                console.log(error.response.headers);
            } else if (error.request) {
                // The request was made but no response was received
                console.log(error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log('Error', error.message);
            }
        });
    }    
}

function doFly(item: any, s: any, d: any) {

    const o = d[item.target];
    let duration;
    let current_dir_scale;
    const duration_bits = item.duration.split('|');
    if (duration_bits.length == 2) {
        duration = parseInt(duration_bits[0]);
        current_dir_scale = parseInt(duration_bits[1]);
    } else {
        duration = parseInt(item.duration);
        current_dir_scale = dir_scale;
    }
    const sphere = search_sphere;
    sphere.position.x = parseFloat(o.xg);
    sphere.position.y = parseFloat(o.zg);
    sphere.position.z = -parseFloat(o.yg);

    // We want to position the camera just slightly in front of the star, so calculate a direction vector

    let dx = camera.position.x - sphere.position.x;
    let dy = camera.position.y - sphere.position.y;
    let dz = camera.position.z - sphere.position.z;

    let mag = Math.sqrt(dx*dx+dy*dy+dz*dz);

    let dir_x = dx/mag;
    let dir_y = dy/mag;
    let dir_z = dz/mag;

    // TODO - add the option of not setting the search sphere
    // Do not show the sphere for the positions

    // if (['Top','Bottom','Right','Left','NearTop','NearBottom','NearRight','NearLeft'].includes(item.target)) {
    //     sphere.visible = false;
    // } else {
    //     sphere.visible = true;
    // }

    sphere.visible = false;

    const aabb = new THREE.Box3().setFromObject( search_sphere );
    const center = aabb.getCenter( new THREE.Vector3() );
    const size = aabb.getSize( new THREE.Vector3() );

    if (duration == 0) {
        camera.position.x = center.x+current_dir_scale*dir_x;
        camera.position.y = center.y+current_dir_scale*dir_y;
        camera.position.z = center.z+current_dir_scale*dir_z;
        handleScript(s,d);
    } else {
        gsap.to( camera.position, {
            duration: duration,
            x: center.x+current_dir_scale*dir_x,
            y: center.y+current_dir_scale*dir_y,
            z: center.z+current_dir_scale*dir_z,
            // onUpdate: function() {
            //     camera.lookAt( center );
            // },
            onComplete: function() {
                controls.enabled = true;
                //controls.target.set( center.x,  center.y, center.z);
                handleScript(s,d);
            }
        } );
    }
}

function doRotate(item: any, s: any, d: any) {

    const o = d[item.target];
    console.log('in doRotate',o);
    const duration = parseInt(item.duration);
    const position = new THREE.Vector3(parseFloat(o.xg),parseFloat(o.zg),-parseFloat(o.yg));

    if (duration == 0) {
        camera.lookAt(position);
        handleScript(s,d);
    } else {
        const camrot = {'x':camera.rotation.x,'y':camera.rotation.y,'z':camera.rotation.z}        

        camera.lookAt(position);
        const targetOrientation = camera.quaternion.clone().normalize();

        camera.rotation.x = camrot.x;
        camera.rotation.y = camrot.y;
        camera.rotation.z = camrot.z;

        controls.enabled = false;   

        const startOrientation = camera.quaternion.clone();

        gsap.to({}, {
            duration: duration,
            onUpdate: function() {
                camera.quaternion.copy(startOrientation).slerp(targetOrientation, this.progress());
            },
            onComplete: function() {
                controls.target.set( position.x, position.y, position.z);
                controls.enabled = true;
                handleScript(s,d);
            }
        });
    }
}

function handleCanvasDblClick(e: any) {
    const p = star_point.point;
    const url = 'https://gruze.org:3009/query?v=2&x='+p.x+'&y='+(-parseFloat(p.z))+'&z='+p.y;
    console.log('star_point_clicked',p);
    console.log('url',url);
    axios({
        url: url,
        method: 'GET',
        responseType: 'json', // important
    }).then((response) => {
        console.log('position search response',response.data);
        processSearchResponse(response, 1, 1);
    });
}

function handleSearchCancel(e: any) {
    e.stopPropagation();
    const search_accordion = document.getElementById('search-accordion');
    console.log('accordion',search_accordion);
    (document.getElementById('search-data')! as HTMLDivElement).style.visibility = 'hidden';
    search_sphere.visible = false;
    controls.enabled = true;
    camera.position.x = 0;
    camera.position.y = 0;
    camera.position.z = 300;
    controls.target.set( 0,0,0 );
    camera.lookAt(0,0,0);
    camera.updateProjectionMatrix();
    //animate();
}

function handleSearchBox(e: any) {
    e.stopPropagation();
    if(e.keyCode === 13) {
        const searchElement = document.getElementById('searchField')! as HTMLInputElement;
        let searchText = searchElement.value.trim();
        searchElement.value = '';
        searchElement.blur(); // remove focus to ensure mobile keyboards are removed
        
        if (/^\d+$/.test(searchText)) {
            searchText = 'Gaia EDR3 '+searchText;
        }

        searchEffect(searchText);
    }
}

function searchEffect(s: string) {

    console.log('in searchEffect',s);
    if (['sun','sol'].includes(s.toLowerCase())) {
        let description;
        description = 'Sun';
        description += '<br /><br /><i>Spectral type:</i><br />G2V';
        description += '<br /><br /><i>Abs. mag.:</i><br />4.83';
        description += '<br /><br />Star not in GCNS catalog.';
        description += '<br /><br />Stars with known planets.';
        description += '<br /><br />Third planet is mostly harmless.';
        handleSearchDisplay('Sun',description,'0','0','0',2,8);
    } else {
        axios({
            url: 'https://gruze.org:3009/query?object='+encodeURIComponent(s),
            method: 'GET',
            responseType: 'json', // important
        }).then((response) => {
            //controls.zoomSpeed = 0.1;
            processSearchResponse(response,2,8);

        }).catch(function (error) {
            if (error.response) {
                // Request made and server responded
                console.log(error.response.data);
                console.log(error.response.status);
                console.log(error.response.headers);
            } else if (error.request) {
                // The request was made but no response was received
                console.log(error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log('Error', error.message);
            }

        });
    }
}

function processSearchResponse(response: any, rotate_time: number, position_time: number) {
    let label;
    let short_name;
    let long_name;
    let n2;
    let n3: string;
    let items;
    let description;

    console.log('lookup response',response);

    const d = response.data;

    console.log('search div',document.getElementById('search-data'));

    let n = d.simbad_name;
    if (!n) {
        n = d.gaia_id;
    }
    short_name = n;
    long_name = n;
    const n_bits = n.split(' ');
    if ((n_bits.length == 2) && (constellations.hasOwnProperty(n_bits[1]))) {
        n3 = n_bits[0];
        if (alphabet.hasOwnProperty(n_bits[0])) {
            n2 = alphabet[n_bits[0]];
        } else {
            n2 = n_bits[0];
        }
        if (short_lookup.hasOwnProperty(n3)) {
            n3 = short_lookup[n3]
        } else if (short_lookup.hasOwnProperty(n3.slice(0,3))) {
            n3 = short_lookup[n3.slice(0,3)]+n3.slice(3);
        }
        short_name = n2 +' '+n_bits[1];
        long_name = n3 +' '+constellations[n_bits[1]];
    }

    if (d.simbad_common) {
        label = d.simbad_common +' ('+short_name+')';
    } else {
        label = short_name;
    }

    description = long_name;
    const distance_in_ly = (parseFloat(d.d)*3.26156).toFixed(3);
    description += '<br /><br /><i>Distance:</i><br />'+d.d+' pc ('+distance_in_ly+' ly)';
    if (d.simbad_spt) {
        description += '<br /><br /><i>Spectral type:</i><br />'+d.simbad_spt;
    }
    if (d.m) {
        description += '<br /><br /><i>Abs. mag.:</i><br />'+d.m;
    }
    /*if (d.id_2mass) {
        description += '<br /><br /><i>2MASS ID:</i><br />'+d.id_2mass;
    }*/

    if (d.gaia_id) {
        description += '<br /><br /><i>Gaia ID:</i><br />'+d.gaia_id;
    }

    const groups = getFilterGroups(d.flythrucode.split('|'));
    console.log('groups',groups);

    if (groups.hasOwnProperty('Non-GCNS stars')) {
        description += '<br /><br />Star not in GCNS catalog.';
        items = groups['Non-GCNS stars'];
        description += '<br />';
        for (let i = 0; i < items.length; i++) {
            description += '<br />'+items[i];
        }
        
    } else {
        description += '<br /><br />Star in GCNS catalog.';
        const keys = Object.keys(groups);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (key != 'Other') {
                description += '<br /><br /><i>'+key+':</i>';
                items = groups[key];
                for (let j = 0; j < items.length; j++) {
                    description += '<br />'+items[j];
                }
            }
        }
        if (groups.hasOwnProperty('Other')) {
            description += '<br /><br /><i>Other:</i>';
            items = groups['Other'];
            for (let i = 0; i < items.length; i++) {
                description += '<br />'+items[i];
            }                
        }
    }

    handleSearchDisplay(label,description,d.xg,d.yg,d.zg,rotate_time,position_time);
}

function handleSearchDisplay(label: string, description: string, xg: string, yg: string, zg: string, rotate_time: number, position_time: number) {
    let duration: number;
    
    document.getElementById('search-label')!.innerHTML = label;
    document.getElementById('search-text')!.innerHTML = description;
    (document.getElementById('search-data')! as HTMLDivElement).style.visibility = 'visible';

    const sphere = search_sphere;
    sphere.position.x = parseFloat(xg);
    sphere.position.y = parseFloat(zg);
    sphere.position.z = -parseFloat(yg);

    sphere.visible = true;

    // We want to position the camera just slightly in front of the star, so calculate a direction vector

    let dx = camera.position.x - sphere.position.x;
    let dy = camera.position.y - sphere.position.y;
    let dz = camera.position.z - sphere.position.z;

    let mag = Math.sqrt(dx*dx+dy*dy+dz*dz);

    if (mag < 10) {
        duration = 2;
    } else {
        duration = 4;
    }

    let dir_x = dx/mag;
    let dir_y = dy/mag;
    let dir_z = dz/mag;

    const camrot = {'x':camera.rotation.x,'y':camera.rotation.y,'z':camera.rotation.z}

    camera.lookAt(sphere.position);
    const targetOrientation = camera.quaternion.clone().normalize();

    camera.rotation.x = camrot.x;
    camera.rotation.y = camrot.y;
    camera.rotation.z = camrot.z;

    const aabb = new THREE.Box3().setFromObject( sphere );
    const center = aabb.getCenter( new THREE.Vector3() );
    const size = aabb.getSize( new THREE.Vector3() );

    controls.enabled = false;   

    const startOrientation = camera.quaternion.clone();

    gsap.to({}, {
        duration: 1,
        onUpdate: function() {
            camera.quaternion.copy(startOrientation).slerp(targetOrientation, this.progress());
        },
        onComplete: function() {
            gsap.to( camera.position, {
                duration: duration,
                x: center.x+dir_scale*dir_x,
                y: center.y+dir_scale*dir_y,
                z: center.z+dir_scale*dir_z,
                onUpdate: function() {
                    camera.lookAt( center );
                },
                onComplete: function() {
                    controls.enabled = true;
                    controls.target.set( center.x,  center.y, center.z);
                }
            } );
        }
    });
}

function onWindowResize() {

    windowHalfX = window.innerWidth / 2;
    windowHalfY = window.innerHeight / 2;

    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();

    renderer.setSize(window.innerWidth, window.innerHeight);

}

function onDocumentMouseMove( event: any ) {

    event.preventDefault();

    mouse.x = ( event.clientX / window.innerWidth ) * 2 - 1;
    mouse.y = - ( event.clientY / window.innerHeight ) * 2 + 1;

}

function onDocumentMouseWheel(event: any) {

    camera.position.z -= event.wheelDeltaY * 0.01;

}

function onPointerMove(event: any) {
    //console.log('mouse move');

    if (event.isPrimary === false) return;

    // mouseX = event.clientX - windowHalfX;
    // mouseY = event.clientY - windowHalfY;

}

//

function animate() {
    const optionsUpdate = ((document.getElementById('gaia-filter-update')!) as HTMLInputElement);
    
    if ((g_keys.length > 0) && (optionsUpdate.value == '1')) {
        const options = ((document.getElementById('gaia-filter')!) as HTMLInputElement).value;
        console.log('setting filter, options: ', options);
        if (options) {
            const option_bits = options.split('|');
            filterStars(option_bits);
        }
        optionsUpdate.value = '0';
    }

    const meshes_loaded = (Object.keys(mesh_list).length == mesh_names.length + mesh_names_extra.length);
    // const meshes_loaded = (Object.keys(mesh_list).length == 10);

    const optionsUpdateMesh = ((document.getElementById('gaia-filter-updateMesh')!) as HTMLInputElement);
    
    if (meshes_loaded && (optionsUpdateMesh.value == '1')) {
        const options = ((document.getElementById('gaia-filterMesh')!) as HTMLInputElement).value;
        console.log('setting filterMesh, options: ', options);

        const option_bits = options.split('|');
        filterMeshes(option_bits);
        optionsUpdateMesh.value = '0';
    }

    const scriptDataUpdate = ((document.getElementById('gaia-script-update')!) as HTMLInputElement);
    
    if ( (g_keys.length > 0) && meshes_loaded && (scriptDataUpdate.value == '1')) {
        const scriptData = ((document.getElementById('gaia-script')!) as HTMLInputElement).value;
        const scriptImmediate = ((document.getElementById('gaia-script-immediate')!) as HTMLInputElement).value;
        console.log('setting script, data: ', scriptData);
        setScriptFile(scriptData,scriptImmediate);
        scriptDataUpdate.value = '0';
    }

    const starScaleUpdate = ((document.getElementById('gaia-star-scale-update')!) as HTMLInputElement);
    
    if (starScaleUpdate.value == '1') {
        const starScale = ((document.getElementById('gaia-star-scale')!) as HTMLInputElement).value;
        console.log('setting star scale: ', starScale);
        setStarScale(starScale);
        starScaleUpdate.value = '0';
    }

    //console.log('in animate');

    requestAnimationFrame(animate);

    render();
    //stats.update();

}

function render() {

    // const time = Date.now() * 0.00005;

    // camera.position.x += (mouseX - camera.position.x) * 0.05;
    // camera.position.y += (- mouseY - camera.position.y) * 0.05;

    // camera.lookAt(scene.position);

    raycaster.setFromCamera( mouse, camera );

    const intersections = raycaster.intersectObjects( scene.children );
    intersection = ( intersections.length ) > 0 ? intersections[ 0 ] : null;
    if (intersection) {
        star_point = intersection;
        //console.log(intersection);
    }

    if (doSpin) {
        controls.update();
    }

    renderer.render(scene, camera);

}

export { init, animate };